import React, {
    useEffect,
    useState,
} from 'react';
import { FaHeadset } from 'react-icons/fa';
import {
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { Menu, Power, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import logoGolden from '../assets/images/Logo-golden.png';
import LogoutModel from './LogoutModel';
import { capitalizeName } from '../utils/capitalize';

const Navbar: React.FC = () => {
    
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
    const name = useSelector((state: RootState) => state.user.first_name);
    const [greeting, setGreeting] = useState<string>("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [isLogout, setIsLogout] = useState(false);

    const is_profile_completed = localStorage.getItem("is_profile_completed");
    const isOnboardingRoute = location.pathname === '/onboarding';
    const isHomeRoute = location.pathname.includes('home');

    useEffect(() => {
        if (isHomeRoute) {
            const time = new Date().getHours();
            if (time < 12) {
                setGreeting("Morning");
            }
            else if (time < 18) {
                setGreeting("Afternoon");
            }
            else {
                setGreeting("Evening");
            }
        }
    }, [location.pathname]);

    const handleAboutUs = () => {
        navigate('/');
        // Set a small timeout to ensure navigation completes
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('scrollToSection', {
                detail: { section: 'aboutus' }
            }));
        }, 100);
    }

    const handleContactUs = () => {
        navigate('/contact-us');
    }

    const handleHome = () => {
        if (!isAuthenticated) {
            navigate('login');
        }

        if (is_profile_completed !== undefined) {
            navigate('/home');
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('scrollToSection', {
                    detail: { section: 'home' }
                }));
            }, 100);
        } else {
            navigate("/onboarding", {
                state: { directToOnboarding: true },
                replace: true,
            });
        }
    }

    const handleNavigateRegister = () => {
        navigate('/');
        // Set a small timeout to ensure navigation completes
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('scrollToSection', {
                detail: { section: 'register' }
            }));
        }, 100);
    }

    return (
        <>
            <nav className="bg-primary sticky top-0 z-30">
                <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo/Title Section */}
                        <div className="flex items-center gap-8 h-fit">
                            <span className=''>
                                <img src={logoGolden} alt='logo-golden' className='w-72 h-18 ' />
                            </span>

                            {/* greeting section */}
                            {
                                isHomeRoute && (
                                    <span className='text-secondary font-outfit text-sm md:text-md lg:text-xl mt-1'>{`Hi, Good ${greeting} `} {name ? capitalizeName(name) : '' }</span>
                                )
                            }

                            {/* menu section */}
                            {(!isOnboardingRoute && !isHomeRoute) && (
                                < div className='hidden lg:block'>
                                    <ul className="flex space-x-6 text-secondary font-outfit font-normal text-sm md:text-base">
                                        <li><button onClick={handleHome} className="hover:text-golden transition-colors duration-300">
                                            Home
                                        </button></li>
                                        <li><button onClick={handleAboutUs} className="hover:text-golden transition-colors duration-300">
                                            About Us
                                        </button></li>
                                        <li><button onClick={handleContactUs} className="hover:text-golden transition-colors duration-300">
                                            Contact Us
                                        </button></li>
                                    </ul>
                                </div>
                            )}

                        </div>

                        {/* Navigation Items */}
                        <div className="flex items-center space-x-4 md:space-x-6">
                            {
                                (!isOnboardingRoute && !isHomeRoute) && (
                                    <>
                                        {
                                            !isAuthenticated && (
                                                <span
                                                    onClick={handleNavigateRegister}
                                                    className="hidden lg:block text-secondary text-xs md:text-base cursor-pointer hover:text-goldenHover transition-colors duration-150"
                                                >
                                                    Not registered?
                                                </span>
                                            )
                                        }

                                        <button
                                            onClick={() => {
                                                if (isAuthenticated) {
                                                    if (is_profile_completed) {
                                                        navigate('/home');
                                                        setTimeout(() => {
                                                            window.dispatchEvent(new CustomEvent('scrollToSection', {
                                                                detail: { section: 'home' }
                                                            }));
                                                        }, 100);
                                                    } else {
                                                        navigate("/onboarding", {
                                                            state: { directToOnboarding: true },
                                                            replace: true,
                                                        });
                                                    }
                                                } else {
                                                    navigate('login');
                                                }
                                            }}
                                            className="px-4 md:px-6 py-2 font-medium text-xs md:text-base 
                  transition-colors duration-300 bg-golden hover:bg-goldenHover 
                  text-primary rounded-full shadow-sm hover:shadow-md"
                                        >
                                            {isAuthenticated ? "HOME" : "LOGIN"}
                                        </button>

                                        {/* Menu Icon for Small Screens */}
                                        <div
                                            className=' top-4 right-4 cursor-pointer text-secondary lg:hidden z-30'
                                            onClick={() => setMenuOpen(!menuOpen)}
                                        >
                                            {menuOpen ? <X className='w-8 h-8 text-secondary' /> : <Menu className='w-8 h-8' />}
                                        </div>

                                        <AnimatePresence>
                                            {menuOpen && (
                                                <motion.div
                                                    initial={{ x: '100%' }}
                                                    animate={{ x: 0 }}
                                                    exit={{ x: '100%' }}
                                                    transition={{ duration: 0.3 }}
                                                    className="fixed right-0 top-16 h-screen w-fit bg-primary z-[20]  shadow-all flex py-6"
                                                >
                                                    <ul className="space-y-4 text-secondary font-outfit  text-md w-full mx-10">
                                                        <li>
                                                            <button onClick={() => {
                                                                setMenuOpen(!menuOpen)
                                                                handleHome()
                                                            }}
                                                                className="hover:text-golden transition-colors duration-300 w-full text-left">
                                                                Home
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button onClick={() => {
                                                                setMenuOpen(!menuOpen)
                                                                handleNavigateRegister()
                                                            }}
                                                                className="hover:text-golden transition-colors duration-300 w-full text-left">
                                                                Register
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button onClick={() => {
                                                                setMenuOpen(!menuOpen)
                                                                handleAboutUs()
                                                            }}
                                                                className="hover:text-golden transition-colors duration-300 w-full text-left">
                                                                About Us
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button onClick={() => {
                                                                setMenuOpen(!menuOpen)
                                                                handleContactUs()
                                                            }}
                                                                className="hover:text-golden transition-colors duration-300 w-full text-left">
                                                                Contact Us
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button onClick={() => {
                                                                setMenuOpen(!menuOpen);
                                                                window.open(`https://wa.me/+918593964196?text=Hello, I need help.`, '_blank');
                                                            }}
                                                                className="hover:text-golden transition-colors duration-300 w-full text-left">
                                                                Help
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                )
                            }

                            {/* Help Section */}
                            {!isHomeRoute && (
                                <div className={`${!isOnboardingRoute ? 'hidden lg:block' : ''} flex gap-3`}>
                                    {isOnboardingRoute && (
                                        <button className='p-2 font-medium text-xs md:text-base 
                  transition-colors duration-300 bg-golden hover:bg-goldenHover 
                  text-primary rounded-full shadow-sm hover:shadow-md'
                                            onClick={() => setIsLogout(true)}>
                                            <Power strokeWidth={3} />
                                        </button>
                                    )}
                                    <button className="flex items-center space-x-2 text-secondary 
                hover:text-goldenHover transition-colors duration-150 
                text-xs md:text-base group"
                                        onClick={() => {
                                            window.open(`https://wa.me/8593964196?text=Hello, I need help.`, '_blank');
                                        }}
                                    >
                                        <span>Help</span>
                                        <FaHeadset className="w-4 h-4 md:w-5 md:h-5 
                  group-hover:scale-110 transition-transform duration-150" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav >

            <LogoutModel
                isOpen={isLogout}
                onClose={() => {
                    setIsLogout(false);
                }}
            />
        </>
    );
};

export default Navbar;
